import { Injectable, inject } from '@angular/core';
import { AuthService } from '../../pages/auth-pages/services/auth.service';
import { PermissionService } from './permission.service';
import {
  ALL_MENU_SECTIONS,
  MenuSection,
  NavItem,
} from '../layout/app-sidebar/menu-config';

/**
 * Fuente ÚNICA de la visibilidad del menú.
 *
 * La consumen el sidebar (`AppSidebarComponent`) y el buscador global
 * (`GlobalSearchService`); si solo la aplicara el sidebar, un rol sin acceso
 * podría llegar igual a la pantalla escribiendo su nombre en el buscador.
 *
 * Tres filtros, en este orden:
 *
 *  1. **Por rol** — reglas duras para lo que NO se apoya en la matriz RBAC,
 *     porque el backend tampoco lo hace (`requireSuperAdmin()` o `@RequiresRole`
 *     sobre catálogos globales de plataforma):
 *     - `/businesses` (administración de las empresas cliente de la plataforma)
 *       solo Super Admin (INC_CRM_024 inc02).
 *     - Config. de correo / políticas de canal: Super Admin; límites de envío y
 *       WhatsApp setup: Admin o superior.
 *     - `/access-requests` (bandeja de solicitudes de acceso, tabla global sin
 *       businessId): solo Super Admin.
 *  2. **Por permisos** — `moduleCode` → `canView` de la matriz del rol. Si el
 *     ítem declara varios códigos, hacen falta TODOS (Y lógica).
 *  3. **Modo avanzado** — herramientas técnicas marcadas `advanced`.
 *
 * La sección **Configuración** ya NO se filtra por nombre de rol. Lo hacía
 * (INC_CRM_024 inc03) y eso dejaba muertos permisos concedidos de verdad: V196
 * dio `users` a Manager para que atendiera las altas y V204 le da `audit`, pero
 * el menú se los ocultaba. Ahora manda la matriz, y los catálogos de
 * configuración exigen `settings` además del módulo de su entidad
 * (`menu-config.ts`), que es lo que mantiene la sección fuera del alcance de
 * Sales Rep y User.
 *
 * Ojo: esto es solo UX. El backend enforza igual (403) y las rutas sensibles
 * tienen `superAdminGuard` / `adminGuard`.
 */
@Injectable({ providedIn: 'root' })
export class MenuVisibilityService {
  private readonly auth = inject(AuthService);
  private readonly permissions = inject(PermissionService);

  get roleName(): string {
    return this.auth.getCurrentUser()?.roleName ?? '';
  }

  isSuperAdmin(): boolean {
    return this.roleName === 'Super Admin';
  }

  /** Admin o Super Admin. */
  isAdmin(): boolean {
    return this.roleName === 'Admin' || this.isSuperAdmin();
  }

  /**
   * Secciones del menú visibles para el usuario actual. Una sección que se queda
   * sin ítems visibles desaparece: eso es lo que oculta Configuración entera a
   * Sales Rep y User, sin necesidad de una regla por nombre de rol.
   */
  sections(advancedMode: boolean): MenuSection[] {
    return ALL_MENU_SECTIONS_WITH_PREFIX
      .map(section => ({
        ...section,
        items: this.filterItems(section.items, advancedMode),
      }))
      .filter(section => section.items.length > 0);
  }

  /** Aplica los tres filtros a una lista de ítems. */
  filterItems(items: NavItem[], advancedMode: boolean): NavItem[] {
    return this.filterByAdvanced(this.filterByPermission(this.filterByRole(items)), advancedMode);
  }

  /** ¿La ruta está permitida para el rol actual? (reglas duras por path). */
  isPathAllowed(path?: string): boolean {
    if (!path) return true;
    // Administración de las empresas cliente de la plataforma — solo Super Admin.
    if (path === '/businesses') return this.isSuperAdmin();
    if (path === '/email-config') return this.isSuperAdmin();
    if (path === '/admin/email-policies') return this.isSuperAdmin();
    // Bandeja de solicitudes de acceso (sección Configuración): tabla global sin
    // businessId, así que solo Super Admin la ve — un Admin de un cliente vería
    // prospectos de otras empresas. Es la misma regla que aplica el backend con
    // requireSuperAdmin() en AccessRequestController; por eso el ítem del menú no
    // lleva moduleCode: no se apoya en la matriz RBAC.
    if (path === '/access-requests') return this.isSuperAdmin();
    if (path === '/config/email-limits') return this.isAdmin();
    if (path === '/whatsapp/setup') return this.isAdmin();
    // Limpieza masiva de los contactos que creó WhatsApp: el backend exige rol Admin o
    // Super Admin en el preview Y en la ejecución (no basta con whatsapp:VIEW), así que el
    // ítem se oculta en vez de llevar al usuario a un 403.
    if (path === '/whatsapp/limpieza-contactos') return this.isAdmin();
    return true;
  }

  // ---- filtros internos ----------------------------------------------------

  private filterByRole(items: NavItem[]): NavItem[] {
    return items
      .map(item => {
        if (item.subItems && item.subItems.length) {
          const filteredSubs = item.subItems.filter(s => this.isPathAllowed(s.path));
          if (filteredSubs.length === 0) return null;
          return { ...item, subItems: filteredSubs };
        }
        return this.isPathAllowed(item.path) ? item : null;
      })
      .filter((i): i is NavItem => i !== null);
  }

  /**
   * Oculta ítems/subítems cuyo `moduleCode` el rol NO pueda ver.
   * Super Admin ve todo; ítems SIN moduleCode quedan visibles; si todos los
   * subítems con moduleCode quedan ocultos, el grupo se oculta. Si la matriz
   * aún no cargó no ocultamos nada (se recalcula al llegar `permissions$`).
   */
  private filterByPermission(items: NavItem[]): NavItem[] {
    if (!this.permissions.isLoaded() || this.permissions.isSuperAdmin()) {
      return items;
    }

    return items
      .map(item => {
        // El moduleCode del grupo gobierna también sus subítems.
        if (!this.canViewModules(item.moduleCode)) {
          return null;
        }
        if (item.subItems && item.subItems.length) {
          const filteredSubs = item.subItems.filter(s => this.canViewModules(s.moduleCode));
          if (filteredSubs.length === 0) return null;
          return { ...item, subItems: filteredSubs };
        }
        return item;
      })
      .filter((i): i is NavItem => i !== null);
  }

  /**
   * ¿El rol puede ver el/los módulo(s) declarados por un ítem?
   *
   * Sin `moduleCode` el ítem es visible (lo gobierna, si acaso, una regla dura
   * por path). Con varios códigos hacen falta TODOS: los catálogos de
   * Configuración exigen `settings` además del módulo de su entidad, para que
   * tener `contacts` no implique ver Configuración → Config. Contactos.
   */
  private canViewModules(moduleCode?: string | string[]): boolean {
    if (!moduleCode) return true;
    const codes = Array.isArray(moduleCode) ? moduleCode : [moduleCode];
    return codes.every(code => this.permissions.canView(code));
  }

  /**
   * Oculta ítems/subítems marcados `advanced` cuando el modo avanzado está
   * apagado. Si un grupo queda sin subítems visibles, se oculta por completo.
   */
  private filterByAdvanced(items: NavItem[], advancedMode: boolean): NavItem[] {
    if (advancedMode) return items;

    return items
      .map(item => {
        if (item.advanced) return null;
        if (item.subItems && item.subItems.length) {
          const filteredSubs = item.subItems.filter(s => !s.advanced);
          if (filteredSubs.length === 0) return null;
          return { ...item, subItems: filteredSubs };
        }
        return item;
      })
      .filter((i): i is NavItem => i !== null);
  }
}

/**
 * `ALL_MENU_SECTIONS` no lleva `prefix` (lo consumía solo el buscador global).
 * Aquí se le añade el prefijo/rotateIcon que necesita el sidebar, manteniendo
 * `menu-config.ts` como única fuente del contenido del menú.
 */
const SECTION_PREFIXES: Record<string, { prefix: string; rotateIcon?: boolean }> = {
  'Comercio & Ventas':    { prefix: 'sales' },
  'Comunicación':         { prefix: 'comm' },
  'Marketing':            { prefix: 'marketing' },
  'Automatización':       { prefix: 'automation' },
  'Servicio al Cliente':  { prefix: 'service' },
  'Analítica y Reportes': { prefix: 'analytics' },
  'Configuración':        { prefix: 'config', rotateIcon: true },
};

const ALL_MENU_SECTIONS_WITH_PREFIX: MenuSection[] = ALL_MENU_SECTIONS.map(s => ({
  title: s.title,
  prefix: SECTION_PREFIXES[s.title]?.prefix ?? s.title.toLowerCase(),
  rotateIcon: SECTION_PREFIXES[s.title]?.rotateIcon,
  items: s.items,
}));
